import { gql } from "lib/apolloClient";
import {
  fieldFragment,
  plannerElementSetFragment,
  programmeInsightsFragment,
} from "./Fragments";
import {
  plannerFieldFragment,
  plannerMediaFieldFragment,
} from "CommonGraphql/PlannerField/PlannerFieldFragments";
import { plannerElementNodeFragment } from "CommonGraphql/PlannerElementNode/PlannerElementNodeFragments";
import {
  pypElementFragment,
  resolvedFieldValueFragment,
} from "modules/CommonFragments";

export const getPlannerElementSetQuery = gql`
  query getPlannerElementSet($id: ID!, $filters: PlannerElementFilters) {
    node(id: $id, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        id
        label
        type
        nodes(filters: $filters) {
          id
          label
          subText
          code
          type
          subText
          parent
          children
          depth
          associatedParents {
            id
          }
        }
      }
    }
  }
`;

export const getCustomPlannerElementSetsQuery = gql`
  query getCustomPlannerElementSets(
    $id: ID!
    $curriculumNodeInterfaceType: ENTITY_TYPE_ENUM!
    $category: [PLANNER_ELEMENT_SET_CATEGORY_ENUM!]
    # Need to fix discrepancy in filters from BE
    $filters: PlannerElementSetFilters
    $_filters: PlannerEelementSetFilters
  ) {
    node(id: $id, type: $curriculumNodeInterfaceType) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementSets(category: $category, filters: $_filters) {
          ...plannerElementSetBasicDetailsItem
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementSets(category: $category, filters: $filters) {
          ...plannerElementSetBasicDetailsItem
        }
      }
      ... on IbMypCurriculumProgram {
        id
        plannerElementSets(category: $category, filters: $filters) {
          ...plannerElementSetBasicDetailsItem
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementSets(category: $category, filters: $filters) {
          ...plannerElementSetBasicDetailsItem
        }
      }
    }
  }
  ${plannerElementSetFragment.plannerElementSetBasicDetails}
`;

export const getPlannerElementSetBasicDetailsQuery = gql`
  query getPlannerElementSet($id: ID!, $filters: PlannerElementFilters) {
    node(id: $id, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        id
        nodes(filters: $filters) {
          id
          label
          subText
        }
      }
    }
  }
`;

export const getSubjectGroupPOIQuery = gql`
  query getSubjectGroupPOI(
    $id: ID!
    $filters: SubjectGroupPOIFilterInput
    $uids: [String!]
    $showDetailedView: Boolean!
  ) {
    node(id: $id, type: SUBJECT_GROUP) {
      id
      ... on SubjectGroup {
        id
        poi: plannerElementNodeVerticalInsight(filters: $filters) {
          unitPlanData {
            totalCount
            unitPlans {
              id
            }
          }
          plannerNode {
            id
            type
          }

          edges {
            subject {
              id
              name
            }
            grade {
              id
              name
            }
            unitPlanData {
              unitPlans @include(if: $showDetailedView) {
                id
                fields(uids: $uids) {
                  ...unitPlanFieldBasicDetailsItem
                }
                startDate
                endDate
                __typename
              }
              unitPlans @skip(if: $showDetailedView) {
                id
                __typename
              }
              totalCount
            }
          }
        }
      }
    }
  }
  ${fieldFragment.unitPlanFieldBasicDetails}
`;

export const getSubjectGroupOverviewQuery = gql`
  query getSubjectGroupOverview(
    $id: ID!
    $filters: SubjectGroupOverviewFilterInput
  ) {
    node(id: $id, type: SUBJECT_GROUP) {
      id
      ... on SubjectGroup {
        id
        name
        subjects {
          id
          name
          isArchived
        }
        subjectGroupOverview(filters: $filters) {
          grade {
            id
            name
            displaySequence
          }
          subject {
            id
            name
            isArchived
          }
          unitPlans {
            id
            startDate
            endDate
            templateId
            title {
              ...plannerFieldNodeItem
            }
            image {
              ...mediaFieldNodeItem
            }
            allFields {
              ...unitPlanFieldBasicDetailsItem
            }
          }
        }
      }
    }
  }
  ${plannerFieldFragment.plannerFieldValue}
  ${plannerMediaFieldFragment.mediaFieldNodeValue}
  ${fieldFragment.unitPlanFieldBasicDetails}
`;

export const getSubjectOverviewQuery = gql`
  query getSubjectOverview(
    $id: ID!
    $uids: [String!]
    $filters: UnitPlanFilter
  ) {
    node(id: $id, type: SUBJECT) {
      ... on Subject {
        id
        grades {
          id
          name
          unitPlans(filters: $filters) {
            id
            templateId
            startDate
            endDate
            title {
              ...plannerFieldNodeItem
            }
            image {
              ...mediaFieldNodeItem
            }
            subjects {
              id
              value
            }
            fields(uids: $uids) {
              ...unitPlanFieldBasicDetailsItem
            }
          }
        }
      }
    }
  }
  ${plannerFieldFragment.plannerFieldValue}
  ${plannerMediaFieldFragment.mediaFieldNodeValue}
  ${fieldFragment.unitPlanFieldBasicDetails}
`;

export const getSubjectOverviewV2Query = gql`
  query getSubjectOverviewV2(
    $id: ID!
    $uids: [String!]
    $filters: UnitPlansV2FeedFilters!
    $first: Int
    $after: String
    $orderBy: OrderByEnum
    $orderByDirection: OrderByDirectionEnum
    $includeAllFields: Boolean!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        unitPlansV2(
          filters: $filters
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
            __typename
          }
          edges {
            node {
              id
              templateId
              startDate
              endDate
              title {
                ...plannerFieldNodeItem
              }
              image {
                ...mediaFieldNodeItem
              }
              subjects {
                id
                value
              }
              fields(uids: $uids) {
                ...unitPlanFieldBasicDetailsItem
              }
              allFields @include(if: $includeAllFields) {
                ...unitPlanFieldBasicDetailsItem
              }
            }
          }
        }
      }
    }
  }

  ${plannerFieldFragment.plannerFieldValue}
  ${plannerMediaFieldFragment.mediaFieldNodeValue}
  ${fieldFragment.unitPlanFieldBasicDetails}
`;

export const getSubjectPOIQuery = gql`
  query getSubjectPOI($id: ID!, $filters: SubjectVerticalOverviewFilters) {
    node(id: $id, type: SUBJECT) {
      ... on Subject {
        id
        verticalOverview: plannerElementNodeVerticalInsight(filters: $filters) {
          plannerNode {
            id
          }
          edges {
            grade {
              id
              name
            }
            unitPlanData {
              unitPlans {
                id
              }
              totalCount
            }
            assessmentData {
              assessments {
                id
              }
              totalCount
            }
            questionsData {
              questions {
                id
                assessmentDataV2 {
                  id
                }
              }
              totalCount
            }
          }
        }
      }
    }
  }
`;

export const getSubjectGroupPoiV2Query = gql`
  query getSubjectGroupPoiV2($id: ID!, $filters: SubjectGroupPOIFilterInput) {
    node(id: $id, type: SUBJECT_GROUP) {
      id
      ... on SubjectGroup {
        id
        plannerElementNodeVerticalInsight(filters: $filters) {
          plannerNode {
            ...plannerElementNodeItem
          }
          edges {
            subject {
              id
              name
            }
            grade {
              id
            }
            unitPlanData {
              unitPlans {
                id
              }
              totalCount
            }
            assessmentData {
              assessments {
                id
              }
              totalCount
            }
            questionsData {
              questions {
                id
                assessmentDataV2 {
                  id
                }
              }
              totalCount
            }
          }
        }
      }
    }
  }
  ${plannerElementNodeFragment.plannerElementNode}
`;

export const getHorizontalViewQuery = gql`
  query getHorizontalView(
    $id: ID!
    $uids: [String!]
    $detailedView: Boolean = true
    $isDP: Boolean = false
    $filters: HorizontalViewFilterInput
    $subjectFilters: SubjectFilters
  ) {
    node(id: $id, type: GRADE) {
      id
      ... on Grade {
        id
        name
        subjects(filters: $subjectFilters) @skip(if: $isDP) {
          id
          name
          isArchived
          subjectGroup {
            id
          }
        }
        subjects(filters: $subjectFilters) @include(if: $isDP) {
          id
          name
          type
          isArchived
          subjectGroup {
            id
            name
          }
          variants {
            level {
              id
              label
            }
          }
          contentSubject {
            id
            assessmentYearTitle
          }
        }
        plannerElementNodeHorizontalInsight(filters: $filters) {
          plannerNode {
            id
            label
            type
          }
          edges {
            subject {
              id
            }
            unitPlans @include(if: $detailedView) {
              id
              fields(uids: $uids) {
                ...unitPlanFieldDetails
              }
              startDate
              endDate
            }
            unitPlans @skip(if: $detailedView) {
              id
            }
            totalCount
          }
          unitPlanData {
            totalCount
            unitPlans {
              id
            }
          }
        }
      }
    }
  }
  ${fieldFragment.unitPlanFieldDetails}
`;

export const getUnitPlansOfSubjectAcrossGradesQuery = gql`
  query unitPlansOfSubjectAcrossGrades($id: ID!, $filters: UnitPlanFilter) {
    node(id: $id, type: SUBJECT) {
      ... on Subject {
        id
        name
        grades {
          id
          name
          unitPlans(filters: $filters) {
            id
            unitType {
              ...plannerFieldNodeItem
            }
            title {
              ...plannerFieldNodeItem
            }
            image {
              ...mediaFieldNodeItem
            }
            startDate
            endDate
          }
        }
      }
    }
  }
  ${plannerFieldFragment.plannerFieldValue}
  ${plannerMediaFieldFragment.mediaFieldNodeValue}
`;

export const getGradesOfSubjectQuery = gql`
  query getGradesOfSubject($id: ID!) {
    node(id: $id, type: SUBJECT) {
      ... on Subject {
        id
        name
        grades {
          id
          name
          displaySequence
        }
      }
    }
  }
`;

export const getOrganizationPlannerTemplatesByGradesQuery = gql`
  query getOrganizationPlannerTemplatesByGrades(
    $id: ID!
    $gradeIds: [ID!]
    $plannerTemplateFilters: TemplateFilters
    $templateFilters: OrganizationTemplateFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        grades(ids: $gradeIds) {
          id
          plannerTemplates(filters: $plannerTemplateFilters) {
            id
            subType
          }
        }
        templates(filters: $templateFilters) {
          id
          body
          unitType
        }
      }
    }
  }
`;
export const getOrganizationSubjectGroupIdsQuery = gql`
  query getOrganizationSubjectGroupIds(
    $id: ID!
    $filters: SubjectGroupFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjectGroups(filters: $filters) {
          id
        }
      }
    }
  }
`;

export const getPlanningInsightDownloadUrlQuery = gql`
  query getPlanningInsightDownloadUrl(
    $id: ID!
    $unitPlanInsightUrlInput: UnitPlanInsightUrlInput!
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        generateUnitPlanInsightUrl(
          unitPlanInsightUrlInputs: $unitPlanInsightUrlInput
        ) {
          url
          isSuccess
        }
      }
    }
  }
`;

export const getOrganizationPlanningInsightDownloadUrlQuery = gql`
  query getOrganizationPlanningInsightDownloadUrl(
    $id: ID!
    $input: genericUnitPlanInsightUrlInput!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        insightsDownloadUrl: genericUnitPlanInsightDownloadUrl(input: $input) {
          url
          isSuccess
        }
        __typename
      }
      __typename
    }
  }
`;

export const getOverallInsightsDownloadQuery = gql`
  query getOrganizationPlanningInsightDownloadUrl(
    $id: ID!
    $input: OverallProgrammeInsightDownloadUrlInput!
    $curriculumProgramType: ENTITY_TYPE_ENUM!
  ) {
    node(id: $id, type: $curriculumProgramType) {
      id
      ... on CurriculumProgram {
        id
        insightsDownloadUrl: overallProgrammeInsightsDownloadUrl(
          input: $input
        ) {
          url
          isSuccess
        }
      }
    }
  }
`;

export const getSubjectGroupTimelineViewQuery = gql`
  query getSubjectGroupTimelineView(
    $id: ID!
    $uids: [String!]
    $filters: SubjectGroupOverviewFilterInput
  ) {
    node(id: $id, type: SUBJECT_GROUP) {
      id
      ... on SubjectGroup {
        id
        name
        subjectGroupOverview(filters: $filters) {
          grade {
            id
            name
          }
          subject {
            id
            name
            isArchived
          }
          unitPlans {
            id
            title {
              id
              uid
              value
            }
            image {
              ...mediaFieldNodeItem
            }
            startDate
            endDate
            fields(uids: $uids) {
              id
              uid
              value
            }
          }
        }
      }
    }
  }
  ${plannerMediaFieldFragment.mediaFieldNodeValue}
`;

export const getOverallProgrammeInsightsQuery = gql`
  query getOverallInsights(
    $curriculumProgramId: ID!
    $curriculumProgramType: ENTITY_TYPE_ENUM!
    $filters: OverallProgrammeInsightFilters!
  ) {
    node(id: $curriculumProgramId, type: $curriculumProgramType) {
      id
      ... on CurriculumProgram {
        overallProgrammeInsights(filters: $filters) {
          plannerNode {
            id
          }
          insights {
            grade {
              id
              name
            }
            unitPlanData {
              unitPlans {
                id
              }
              totalCount
            }
          }
          totalUnitPlanData {
            unitPlans {
              id
            }
            totalCount
          }
        }
      }
    }
  }
`;

export const getOrganizationSubjectsBasicDetailsQuery = gql`
  query getOrganizationSubjectsBasicDetails(
    $organizationId: ID!
    $filters: SubjectFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjects(filters: $filters) {
          id
          name
          isArchived
          subjectGroup {
            id
            name
            displaySequence
          }
        }
      }
    }
  }
`;

export const getSubjectInsightsQuery = gql`
  query getSubjectInsights($id: ID!, $filters: UnitPlanInsightsFilter) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        unitPlanInsights(filters: $filters) {
          totalCount
          plannerNode {
            id
          }
          unitPlans {
            id
          }
        }
      }
    }
  }
`;

export const getUnitPlansOfGradeQuery = gql`
  query getUnitPlansOfGrade(
    $id: ID!
    $filters: UnitPlanFilter
    $subjectFilters: SubjectFilters
    $uids: [String!]
    $isDP: Boolean = false
  ) {
    node(id: $id, type: GRADE) {
      id
      ... on Grade {
        name
        subjects(filters: $subjectFilters) @skip(if: $isDP) {
          id
          name
          isArchived
        }
        subjects(filters: $subjectFilters) @include(if: $isDP) {
          id
          name
          type
          subjectGroup {
            id
            name
          }
          variants {
            level {
              id
              label
            }
          }
          contentSubject {
            id
            assessmentYearTitle
          }
          isArchived
        }
        unitPlans(filters: $filters) {
          id
          unitType {
            ...plannerFieldNodeItem
          }
          title {
            ...plannerFieldNodeItem
          }
          duration {
            ...plannerFieldNodeItem
          }
          image {
            ...mediaFieldNodeItem
          }
          subjects {
            ...plannerFieldNodeItem
          }
          fields(uids: $uids) {
            ...plannerFieldNodeItem
            resolvedMinimalTree @include(if: $isDP) {
              id
              ... on ResolvedFieldGenericTags {
                id
                genericTags {
                  id
                  label
                }
              }
            }
          }
          startDate
          endDate
        }
      }
    }
  }
  ${plannerFieldFragment.plannerFieldValue}
  ${plannerMediaFieldFragment.mediaFieldNodeValue}
`;

export const getOrganizationGradesSubjectsQuery = gql`
  query getOrganizationGradesSubjects($id: ID!, $filters: GradeFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        grades(filters: $filters) {
          id
          name
          subjects {
            id
            name
          }
        }
      }
    }
  }
`;

export const getGradeSubjectsWithSubjectGroupQuery = gql`
  query getGradeSubjectsWithSubjectGroup($id: ID!) {
    node(id: $id, type: GRADE) {
      id
      ... on Grade {
        name
        subjects {
          id
          name
          isArchived
          subjectGroup {
            id
            name
          }
        }
      }
    }
  }
`;

export const getOrganizationInsightUnitPlansQuery = gql`
  query getOrganizationInsightUnitPlans(
    $id: ID!
    $filters: UnitPlansV2FeedFilters!
    $fieldUids: [String!]
    $after: String
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        unitPlans: unitPlansV2(
          first: 20
          filters: $filters
          after: $after
          orderBy: GRADE_DISPLAY_SEQUENCE
          orderByDirection: ASC
        ) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title {
                id
                value
              }
              image {
                ...mediaFieldNodeItem
              }
              theme {
                id
                value
              }
              unitType {
                id
                value
              }
              subjects {
                id
                value
                resolvedMinimalTree {
                  ... on ResolvedFieldSubjectSet {
                    id
                    subjects {
                      id
                      name
                      isArchived
                    }
                  }
                }
              }
              fields(uids: $fieldUids) {
                id
                uid
                value
                resolvedMinimalTree {
                  ... on ResolvedFieldGenericTags {
                    id
                    genericTags {
                      id
                      label
                    }
                  }
                  ... on ResolvedFieldObjectiveLevel {
                    id
                    nodes {
                      id
                      label
                      associatedParents {
                        id
                      }
                    }
                  }
                }
              }
              isIncludedInInsights
              grades
            }
          }
        }
      }
    }
  }
  ${plannerMediaFieldFragment.mediaFieldNodeValue}
`;

export const getInsightsIncludedUnitCountQuery = gql`
  query getInsightsIncludedUnitCount(
    $id: ID!
    $curriculumProgramIds: [ID!]
    $academicYears: [ID!]
    $fetchTransdisciplinaryCount: Boolean!
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        totalUnitPlanCount: unitPlansV2(
          filters: {
            curriculumProgramIds: $curriculumProgramIds
            academicYears: $academicYears
            hasClass: true
          }
        ) {
          totalCount
        }
        totalExcludedCount: unitPlansV2(
          filters: {
            curriculumProgramIds: $curriculumProgramIds
            academicYears: $academicYears
            isIncludedInInsights: false
            hasClass: true
          }
        ) {
          totalCount
        }
        totalIncludedCount: unitPlansV2(
          filters: {
            curriculumProgramIds: $curriculumProgramIds
            academicYears: $academicYears
            isIncludedInInsights: true
            hasClass: true
          }
        ) {
          totalCount
        }
        transdisciplinaryIncludedCount: unitPlansV2(
          filters: {
            curriculumProgramIds: $curriculumProgramIds
            academicYears: $academicYears
            isIncludedInInsights: true
            unitType: "ibInquiry"
            hasClass: true
          }
        ) @include(if: $fetchTransdisciplinaryCount) {
          totalCount
        }
      }
    }
  }
`;

export const getOrganizationGradesWithUnitCountQuery = gql`
  query getOrganizationGradesWithUnitCount(
    $id: ID!
    $filters: GradeFilterInput
    $unitPlanFilters: UnitPlanFilter
    $searchText: String
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $filters) {
          id
          name
          displaySequence
          unitPlans(filters: $unitPlanFilters, searchText: $searchText) {
            id
            isIncludedInInsights
          }
        }
      }
    }
  }
`;

export const getGradePOIQuery = gql`
  query getGradePOI(
    $id: ID!
    $uids: [String!]
    $filters: OverallProgrammeInsightFilters
  ) {
    node(id: $id, type: IB_PYP_CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        ... on IbPypCurriculumProgram {
          id
          gradePOI(filters: $filters) {
            theme {
              id
              label
            }
            grade {
              id
              name
            }
            unitPlans {
              id
              startDate
              endDate
              subjects {
                id
                value
              }
              fields(uids: $uids) {
                id
                uid
                value
                resolvedMinimalTree {
                  id
                  ... on ResolvedFieldRelatedConceptSet {
                    id
                    relatedConcepts {
                      ...relatedConceptItem
                    }
                  }
                  ... on ResolvedFieldConceptSet {
                    id
                    type
                    concepts {
                      ...conceptItem
                    }
                  }
                  ... on ResolvedFieldLPSet {
                    id
                    type
                    learnerProfiles {
                      ...lpItem
                    }
                  }
                  ... on ResolvedFieldATLSet {
                    id
                    type
                    atls {
                      ...atlItem
                    }
                  }
                  ...resolvedFieldVideoAttachmentItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${pypElementFragment.relatedConcept}
  ${pypElementFragment.concept}
  ${pypElementFragment.lp}
  ${pypElementFragment.atl}
  ${resolvedFieldValueFragment.resolvedFieldVideoAttachment}
`;

export const getPypPlannerThemeNodesQuery = gql`
  query getPypPlannerThemeNodes(
    $id: ID!
    $elementTypes: [String!]
    $type: ENTITY_TYPE_ENUM!
    $filters: PlannerBenchmarkFilter
    $themeFilters: PlannerThemeFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbPypCurriculumProgram {
        id
        plannerElementSets(elementTypes: $elementTypes) {
          id
          type
          label
          ... on PlannerThemeSet {
            nodes(filters: $themeFilters) {
              id
              label
              descriptor
              focusAreas {
                id
                label
              }
            }
          }
          ... on PlannerBenchmarkSet {
            rootNodes
            nodes(filters: $filters) {
              id
              label
              subText
              code
              type
              parent
              children
              depth
              grades {
                id
              }
              subject
            }
          }
        }
      }
    }
  }
`;

export const getPYPProgrammeInsightsQuery = gql`
  query getPYPProgrammeInsights(
    $id: ID!
    $filters: OverallProgrammeInsightFilters
  ) {
    node(id: $id, type: IB_PYP_CURRICULUM_PROGRAM) {
      id
      ... on CurriculumProgram {
        id
        ... on IbPypCurriculumProgram {
          id
          programmeInsights(filters: $filters) {
            ... on ATLInsight {
              type
              insights {
                element {
                  id
                  label
                }
                programmeInsightEdge {
                  ...programmeInsightEdgeItem
                }
              }
            }
            ... on ThemeDescriptorInsight {
              type
              insights {
                element {
                  id
                  label
                }
                programmeInsightEdge {
                  ...programmeInsightEdgeItem
                }
              }
            }
            ... on SubjectInsight {
              type
              insights {
                subject {
                  id
                  name
                }
                programmeInsightEdge {
                  ...programmeInsightEdgeItem
                }
              }
            }
            ... on LPAInsight {
              type
              insights {
                element {
                  id
                  label
                }
                programmeInsightEdge {
                  ...programmeInsightEdgeItem
                }
              }
            }
            ... on ConceptInsight {
              type
              insights {
                element {
                  id
                  label
                }
                programmeInsightEdge {
                  ...programmeInsightEdgeItem
                }
              }
            }
            ... on CustomDataSetInsight {
              type
              insights {
                element {
                  id
                  label
                }
                programmeInsightEdge {
                  ...programmeInsightEdgeItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${programmeInsightsFragment.programmeInsightsEdge}
`;

export const getBenchmarkInsightQuery = gql`
  query getBenchmarkInsight(
    $id: ID!
    $filters: BenchmarkVerticalInsightFilters
  ) {
    node(id: $id, type: SUBJECT) {
      id
      ... on Subject {
        id
        benchmarkVerticalInsight(filters: $filters) {
          element {
            id
            label
          }
          edges {
            grade {
              id
            }
            unitPlans {
              id
            }
            assessments {
              id
            }
            questions {
              id
              assessmentDataV2 {
                id
              }
            }
          }
        }
      }
    }
  }
`;

export const getGradeBasicDetailsQuery = gql`
  query getGradeBasicDetails($gradeFilters: GradeFilterInput, $id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        grades(filters: $gradeFilters) {
          id
          name
          subjects {
            id
            name
            isArchived
            subjectGroup {
              id
              name
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationTemplateIdsQuery = gql`
  query getOrganizationTemplateIds(
    $id: ID!
    $filters: OrganizationTemplateFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        templates(filters: $filters) {
          id
          baseTemplate {
            id
          }
        }
      }
    }
  }
`;
